import { useEffect, useState } from "react";

export const EVENT_ATTENDANCE_OPENS_BEFORE_MS = 2 * 60 * 60 * 1000;

const MAX_BROWSER_TIMEOUT_MS = 2_147_000_000;

export function getEventAttendanceOpenAtMs(eventDateTime) {
  if (!eventDateTime) return null;
  const startMs = new Date(eventDateTime).getTime();
  if (!Number.isFinite(startMs)) return null;
  return startMs - EVENT_ATTENDANCE_OPENS_BEFORE_MS;
}

export function isEventAttendanceWindowOpen(eventDateTime, nowMs = Date.now()) {
  const opensAtMs = getEventAttendanceOpenAtMs(eventDateTime);
  return opensAtMs !== null && nowMs >= opensAtMs;
}

export function useEventAttendanceBoundaryClock(eventDateTimes = []) {
  const scheduleKey = eventDateTimes.map((value) => String(value || "")).join("|");
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    let timerId = null;
    const openAtTimes = scheduleKey
      .split("|")
      .map(getEventAttendanceOpenAtMs)
      .filter((value) => value !== null)
      .sort((a, b) => a - b);

    const sync = () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }

      const nextNowMs = Date.now();
      setNowMs(nextNowMs);
      const nextBoundary = openAtTimes.find((value) => value > nextNowMs);
      if (nextBoundary !== undefined) {
        timerId = window.setTimeout(
          sync,
          Math.min(
            Math.max(25, nextBoundary - nextNowMs + 25),
            MAX_BROWSER_TIMEOUT_MS
          )
        );
      }
    };

    const syncWhenVisible = () => {
      if (document.visibilityState !== "hidden") sync();
    };

    sync();
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);

    return () => {
      if (timerId !== null) window.clearTimeout(timerId);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [scheduleKey]);

  return nowMs;
}

/**
 * Keeps the check-in entry point in sync with the T-2h boundary.
 *
 * The timeout handles an already-open screen, while focus/visibility listeners
 * immediately correct the state after the app returns from the background.
 */
export default function useEventAttendanceWindow(eventDateTime) {
  const [isOpen, setIsOpen] = useState(() =>
    isEventAttendanceWindowOpen(eventDateTime)
  );

  useEffect(() => {
    let timerId = null;

    const sync = () => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }

      const opensAtMs = getEventAttendanceOpenAtMs(eventDateTime);
      const nowMs = Date.now();
      const nextIsOpen = opensAtMs !== null && nowMs >= opensAtMs;
      setIsOpen(nextIsOpen);

      if (!nextIsOpen && opensAtMs !== null) {
        timerId = window.setTimeout(
          sync,
          Math.min(Math.max(25, opensAtMs - nowMs + 25), MAX_BROWSER_TIMEOUT_MS)
        );
      }
    };

    const syncWhenVisible = () => {
      if (document.visibilityState !== "hidden") sync();
    };

    sync();
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);

    return () => {
      if (timerId !== null) window.clearTimeout(timerId);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [eventDateTime]);

  return isOpen;
}
